import { FC, useState } from "react";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonText,
  Heading,
  HStack,
  Input,
  InputField,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { Folder, FolderType } from "@/types/resources";

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (folder: Folder) => void;
  parentId?: string | null;
  type: FolderType;
}

const CreateFolderModal: FC<CreateFolderModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  parentId,
  type,
}) => {
  const { i18n } = useLocalization();
  const [name, setName] = useState("");

  const { createFolder, error, loading } = API.Folders.useCreateFolder();

  const handleConfirm = async () => {
    if (!name.trim()) return;
    const folder = await createFolder({
      name,
      parent_id: parentId ?? null,
      type,
    });
    if (folder) {
      setName("");
      onCreated(folder);
    }
  };

  const handleClose = () => {
    setName("");
    onClose();
  };

  return (
    <AlertDialog isOpen={isOpen} onClose={handleClose}>
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6">
        <AlertDialogHeader>
          <Heading size="lg">{i18n.t("folders.newFolder")}</Heading>
        </AlertDialogHeader>
        <AlertDialogBody>
          <Input className="border border-outline-200 rounded-xl bg-background-50">
            <InputField
              autoFocus
              onChangeText={setName}
              onSubmitEditing={handleConfirm}
              placeholder={i18n.t("folders.folderNamePlaceholder")}
              returnKeyType="done"
            />
          </Input>
        </AlertDialogBody>
        <AlertDialogFooter>
          <VStack className="gap-2 w-full">
            <HStack className="gap-3 justify-end">
              <Button
                className="rounded-xl"
                isDisabled={loading}
                onPress={handleClose}
                variant="outline"
              >
                <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
              </Button>
              <Button
                className="rounded-xl"
                isDisabled={loading || !name.trim()}
                onPress={handleConfirm}
              >
                <ButtonText>
                  {loading ? i18n.t("misc.creating") : i18n.t("misc.create")}
                </ButtonText>
              </Button>
            </HStack>
            {error && (
              <Text className="text-error-600 text-sm text-right">{error}</Text>
            )}
          </VStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CreateFolderModal;
